import { z } from "zod";
import type { NotificationSettings } from "@/lib/email/types";

/**
 * User Types
 *
 * Domain types for user entities, separate from database types.
 */

// Supported locales
export const LocaleEnum = z.enum(["de-DE", "en-GB"]);
export type UserLocale = z.infer<typeof LocaleEnum>;

// Company type enum
export const CompanyTypeEnum = z.enum(["landowner", "buyer", "both"]);
export type CompanyType = z.infer<typeof CompanyTypeEnum>;

// User profile schema
export const UserProfileSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().nullable(),
  phone: z.string().nullable(),
  phone_verified: z.boolean().default(false),
  phone_verified_at: z.string().datetime().nullable().optional(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  avatar_url: z.string().url().nullable(),
  company_name: z.string().nullable(),
  company_type: CompanyTypeEnum.nullable(),
  address: z.object({
    line1: z.string().nullable(),
    line2: z.string().nullable(),
    postal_code: z.string().nullable(),
    city: z.string().nullable(),
    country: z.string(),
  }),
  preferred_locale: LocaleEnum,
  // New fields
  bio: z.string().nullable().optional(),
  is_verified: z.boolean().default(false),
  profile_public: z.boolean().default(true),
  last_active_at: z.string().datetime().nullable().optional(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type UserProfile = z.infer<typeof UserProfileSchema>;

// Public profile - safe attributes visible to other users
export const PublicProfileSchema = z.object({
  id: z.string().uuid(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  avatar_url: z.string().url().nullable(),
  company_name: z.string().nullable(),
  company_type: CompanyTypeEnum.nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  bio: z.string().nullable(),
  is_verified: z.boolean(),
  member_since: z.string().datetime(),
  rating_average: z.number().nullable(),
  rating_count: z.number(),
  completed_deals: z.number(),
});

export type PublicProfile = z.infer<typeof PublicProfileSchema>;

// User summary for display in lists, cards, etc.
export interface UserSummary {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  company_name: string | null;
}

// Create user profile input
export const CreateUserProfileSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  first_name: z.string().min(1).max(100).optional(),
  last_name: z.string().min(1).max(100).optional(),
  company_name: z.string().max(200).optional(),
  company_type: CompanyTypeEnum.optional(),
  preferred_locale: LocaleEnum.optional(),
});

export type CreateUserProfileInput = z.infer<typeof CreateUserProfileSchema>;

/**
 * The notification preferences as they actually exist in the column.
 *
 * FIVE keys, not three. The column DEFAULT is
 * `{digest_day, email_weekly_digest, email_match_alerts, email_message_alerts,
 * email_deal_updates}` and 1980 of 2036 production rows carry all five — but
 * `NotificationSettings` in `@/lib/email/types` models only the three the
 * profile modal toggles, and `UserRepository.update` REPLACES the whole jsonb.
 * So a three-key save silently destroyed `digest_day` and
 * `email_weekly_digest`, which the weekly-digest SQL reads
 * (`(notification_settings->>'email_weekly_digest')::boolean = true`).
 *
 * The two extra keys are therefore optional-but-accepted: callers that know
 * about them round-trip them, and callers that do not are not forced to invent
 * values. What is NOT allowed is arbitrary junk — the object is closed, so an
 * unbounded blob cannot be written into the column.
 */
export const NotificationSettingsSchema = z
  .object({
    email_match_alerts: z.boolean(),
    email_message_alerts: z.boolean(),
    email_deal_updates: z.boolean(),
    email_weekly_digest: z.boolean().optional(),
    digest_day: z.string().max(20).optional(),
  })
  // `.strict()`, because a plain `z.object` STRIPS unknown keys rather than
  // rejecting them — and the route writes the parsed value into a jsonb column,
  // so silent stripping would have meant an unbounded blob either sailing
  // through (if the raw body were written) or being dropped without telling the
  // caller. Neither is an answer; refusing is.
  .strict();

/**
 * Compile-time link to the shared interface.
 *
 * The schema deliberately has MORE keys than `NotificationSettings` (which
 * models only the three the profile modal toggles), so it cannot `satisfies
 * z.ZodType<NotificationSettings>` — but the three it does share must stay
 * spelled the same, or a rename in `@/lib/email/types` would typecheck here and
 * 400 every profile save at runtime. This fails the build instead.
 */
type _NotificationKeysCovered =
  Exclude<
    keyof NotificationSettings,
    keyof z.infer<typeof NotificationSettingsSchema>
  > extends never
    ? true
    : never;
const _notificationKeysCovered: _NotificationKeysCovered = true;
void _notificationKeysCovered;

// Update user profile input
export const UpdateUserProfileSchema = z.object({
  first_name: z.string().min(1).max(100).optional(),
  last_name: z.string().min(1).max(100).optional(),
  avatar_url: z.string().url().nullable().optional(),
  company_name: z.string().max(200).optional(),
  company_type: CompanyTypeEnum.optional(),
  address_line1: z.string().max(200).optional(),
  address_line2: z.string().max(200).optional(),
  postal_code: z.string().max(20).optional(),
  city: z.string().max(100).optional(),
  country: z.string().length(2).optional(), // ISO country code
  preferred_locale: LocaleEnum.optional(),
  // New fields
  bio: z.string().max(1000).optional(),
  profile_public: z.boolean().optional(),
  tax_id: z.string().max(50).optional(),
  /**
   * Derived from the shared `NotificationSettings` interface rather than
   * restated: `satisfies` makes adding a preference to `@/lib/email/types` a
   * TYPE ERROR here instead of a silent 400 on every profile save.
   */
  notification_settings: NotificationSettingsSchema.optional(),
});

/**
 * What `PATCH /api/user/profile` accepts from a user editing their OWN profile.
 *
 * Separate from `UpdateUserProfileSchema` because the wire shape differs from
 * the domain shape in two ways that are properties of the FORM, not of the
 * aggregate:
 *
 *  - **Blank fields arrive as `null`.** `ProfileModal` builds
 *    `tax_id: formData.tax_id || null` for nine fields, and `address_line2`
 *    and `tax_id` are blank for almost every user. A schema that is `optional()`
 *    but not `nullable()` 400s the ordinary save — which is exactly what the
 *    first version of this did, and no test saw it, because the component suite
 *    stubs `fetch` to answer `ok: true` and the route suite hand-writes bodies
 *    that never contain a `null`.
 *  - **Names may be cleared.** `ContactDetailsEditModal` and
 *    `ProfileVerificationModal` sent `firstName.trim()` with no non-empty
 *    guard, and the previous allow-list let `""` through. `min(1)` would have
 *    turned that into a 400 for a caller this change never intended to touch.
 *    Both components were deleted in 2026-09 (orphaned by the V2 redesign and
 *    never mounted since). The two live PATCH callers today are `ProfileModal`
 *    (sends `null` for blanks) and `DataCheckPageClient` (refuses empty names
 *    before sending), so nothing sends `""` right now — but that is a property
 *    of those callers, not something enforced here. The tolerance stays: it is
 *    the normalisation the next caller would want, and tightening it would only
 *    risk a 400 the moment one of them stops pre-guarding.
 *
 * `""` is normalised to `null` on the way through, which is what the callers
 * already meant by `|| null` and keeps one representation of "empty" in the
 * column.
 *
 * `email` and `phone` are ABSENT, and the route parses this `.strict()`, so
 * sending either is a 400 rather than a silent drop. See the route for what
 * that does and does not guarantee.
 */
const emptyToNull = (v: unknown) => (v === "" ? null : v);
const optionalText = (max: number) =>
  z.preprocess(emptyToNull, z.string().max(max).nullable().optional());

export const SelfProfileUpdateSchema = z.object({
  first_name: optionalText(100),
  last_name: optionalText(100),
  company_name: optionalText(200),
  company_type: z.preprocess(emptyToNull, CompanyTypeEnum.nullable().optional()),
  address_line1: optionalText(200),
  address_line2: optionalText(200),
  postal_code: optionalText(20),
  city: optionalText(100),
  country: z.preprocess(emptyToNull, z.string().length(2).nullable().optional()),
  preferred_locale: z.preprocess(emptyToNull, LocaleEnum.nullable().optional()),
  tax_id: optionalText(50),
  bio: optionalText(1000),
  profile_public: z.boolean().optional(),
  avatar_url: z.string().nullable().optional(),
  notification_settings: NotificationSettingsSchema.optional(),
});

/**
 * NOTE ON `email` / `phone`: deliberately absent, and this is the enforced half
 * of that decision — `PATCH /api/user/profile` parses `.strict()`, so sending
 * either is a 400 rather than a silent drop.
 *
 * They are authentication identity: `syncVerifiedEmail` and
 * `isEmailTakenByAnother` own email, and changing it must not be possible
 * without the OTP round-trip. `phone` is a narrower claim — see the route.
 */

export type UpdateUserProfileInput = z.infer<typeof UpdateUserProfileSchema>;

// User with their offering stats
export interface UserWithStats extends UserProfile {
  stats: {
    total_offerings: number;
    active_offerings: number;
    total_requests: number;
    active_requests: number;
  };
}

// Placeholder email domain used for phone-only registrations
export const PLACEHOLDER_EMAIL_DOMAIN = "@phone.local";

/**
 * Check if an email is a placeholder (system-generated for phone-only users)
 * Placeholder emails follow the pattern: phone_<digits>@phone.local
 */
export function isPlaceholderEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return email.endsWith(PLACEHOLDER_EMAIL_DOMAIN);
}

/**
 * The placeholder e-mail of a phone-only account whose login phone is `phone`
 * (E.164). It must always encode the CURRENT login: a placeholder left on an
 * old number is how that number used to re-link itself (ARCH-055).
 */
export function buildPlaceholderEmail(phone: string): string {
  return `phone_${phone.replace(/\D/g, "")}${PLACEHOLDER_EMAIL_DOMAIN}`;
}

/**
 * Check if a user profile has a real (non-placeholder) email
 */
export function hasRealEmail(profile: UserProfile | null | undefined): boolean {
  if (!profile?.email) return false;
  return !isPlaceholderEmail(profile.email);
}

/**
 * Extract phone number from a placeholder email
 * e.g., "phone_34644164101@phone.local" -> "+34644164101"
 */
export function extractPhoneFromPlaceholderEmail(email: string): string | null {
  if (!isPlaceholderEmail(email)) return null;

  // Extract the part before @phone.local
  const localPart = email.split("@")[0];

  // Remove "phone_" prefix if present
  const digits = localPart.replace(/^phone_/, "");

  // Format as phone number with + prefix
  if (digits && /^\d+$/.test(digits)) {
    return `+${digits}`;
  }

  return null;
}

/**
 * The shared rule for printing "how we reach this account" from its stored
 * email column. Used by the admin surfaces and by the funnel's AuthSection.
 * - If it's a placeholder email (@phone.local), extract and return the phone number
 * - Otherwise, return the email as-is
 *
 * Callers must still treat a returned `type: "email"` that `isPlaceholderEmail`
 * accepts as "no address": an unparseable placeholder falls through to the
 * email branch rather than returning null.
 */
export function formatContactForDisplay(email: string | null | undefined): {
  type: "email" | "phone";
  value: string;
  href: string;
} | null {
  if (!email) return null;

  if (isPlaceholderEmail(email)) {
    const phone = extractPhoneFromPlaceholderEmail(email);
    if (phone) {
      return {
        type: "phone",
        value: phone,
        href: `tel:${phone}`,
      };
    }
  }

  return {
    type: "email",
    value: email,
    href: `mailto:${email}`,
  };
}
